import { Box, Button } from '@mui/material';
import { KEYBOARD_CONNECTED_PROMPT_Z_INDEX } from 'Constants/StackingLevels';
import { useHardwareBackClosesLayer } from 'Hooks/useHardwareBack';
import { SAVE_FAILED_MESSAGE } from 'Utils/playAlong/playInputAccount';

export const KEYBOARD_CONNECTED_QUESTION = 'Keyboard connected. Use it so MuseFlow can hear you?';

export interface KeyboardConnectedPromptProps {
  open: boolean;
  /** Save "A MIDI keyboard" as the answer. */
  onUseIt: () => void;
  /** Keep the answer and do not ask again this session. */
  onNotNow: () => void;
  saving: boolean;
  saveFailed: boolean;
}

/**
 * Asked when a MIDI keyboard connects while the account says the player has
 * none the app can hear. Only the player's answer changes anything.
 */
export default function KeyboardConnectedPrompt({
  open,
  onUseIt,
  onNotNow,
  saving,
  saveFailed,
}: KeyboardConnectedPromptProps) {
  // Android's Back answers "Not now", as a tap outside the prompt does.
  useHardwareBackClosesLayer(open, onNotNow, KEYBOARD_CONNECTED_PROMPT_Z_INDEX);
  if (!open) return null;
  return (
    <Box
      data-testid="keyboard-connected-prompt"
      onClick={onNotNow}
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: KEYBOARD_CONNECTED_PROMPT_Z_INDEX,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        background: 'rgba(4, 11, 19, 0.6)',
      }}
    >
      <Box
        role="dialog"
        aria-modal="true"
        aria-labelledby="keyboard-connected-question"
        onClick={(event) => event.stopPropagation()}
        sx={{
          maxWidth: '440px',
          padding: '24px 28px',
          borderRadius: '12px',
          backgroundColor: '#050B13',
          border: '1px solid #525969',
          boxShadow: '0px 12px 26px rgba(0, 0, 0, 0.38)',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
          fontFamily: 'Lato',
        }}
      >
        <Box
          id="keyboard-connected-question"
          sx={{ color: '#ECECED', fontSize: '20px', lineHeight: 1.4, textAlign: 'center' }}
        >
          {KEYBOARD_CONNECTED_QUESTION}
        </Box>
        {saveFailed && (
          <Box role="alert" sx={{ color: '#F3C6B8', fontSize: '14px', textAlign: 'center' }}>
            {SAVE_FAILED_MESSAGE}
          </Box>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            disabled={saving}
            onClick={onUseIt}
            sx={{
              minHeight: '44px',
              minWidth: '120px',
              borderRadius: '40px',
              backgroundColor: '#CA7C62',
              color: '#050B13',
              fontFamily: 'Lato',
              fontWeight: 700,
              textTransform: 'none',
              '&:hover': { backgroundColor: '#D9917A' },
            }}
          >
            Use it
          </Button>
          <Button
            variant="outlined"
            disabled={saving}
            onClick={onNotNow}
            sx={{
              minHeight: '44px',
              minWidth: '120px',
              borderRadius: '40px',
              borderColor: '#525969',
              color: '#ECECED',
              fontFamily: 'Lato',
              fontWeight: 700,
              textTransform: 'none',
            }}
          >
            Not now
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
